if (!customElements.get('compare-products')) {
  class CompareProducts extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.selected = new Map();
      this.searchSequence = (this.searchSequence || 0) + 1;
      this.limit = Math.max(2, Math.min(6, Number(this.dataset.maxProducts) || 4));
      this.slots = Array(Math.min(3, this.limit)).fill(null);
      this.activeSlot = null;
      this.form = this.querySelector('[data-compare-search]');
      this.input = this.form?.querySelector('[name="q"]');
      this.panel = this.querySelector('[data-compare-picker-panel]');
      this.results = this.querySelector('[data-compare-results]');
      this.status = this.querySelector('[data-compare-status]');
      this.tableContainer = this.querySelector('[data-compare-table]');
      this.headings = this.querySelector('[data-compare-column-headings]');
      this.rows = [...this.querySelectorAll('tbody > tr[data-compare-key]')];
      if (!this.form || !this.panel || !this.results || !this.headings) return;
      this.querySelectorAll('[data-compare-jump]').forEach((link) => {
        link.hidden = !this.rows.some((row) => row.dataset.compareKey === link.dataset.compareJump);
      });
      this.suggestions = this.results.cloneNode(true);
      this.results.removeAttribute('aria-busy');
      const initial = [...this.headings.querySelectorAll('[data-compare-initial]')];
      initial.slice(0, this.limit).forEach((heading, slot) => {
        const id = heading.dataset.productId;
        if (!id || this.selected.has(id)) return;
        const cells = new Map();
        this.rows.forEach((row) => {
          const cell = [...row.querySelectorAll('td[data-product-id]')].find((item) => item.dataset.productId === id);
          if (cell) cells.set(row.dataset.compareKey, cell.cloneNode(true));
        });
        const entry = {
          id, slot, title: heading.dataset.productTitle || '', cells,
          hero: heading.querySelector('[data-compare-product-hero]')?.cloneNode(true),
          request: new AbortController()
        };
        this.selected.set(id, entry);
        this.slots[slot] = entry;
      });
      const options = { signal: this.events.signal };
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        clearTimeout(this.searchTimer);
        this.search(this.input.value.trim());
      }, options);
      this.input.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchRequest?.abort();
        this.searchSequence += 1;
        this.searchTimer = setTimeout(() => this.search(this.input.value.trim()), 250);
      }, options);
      this.input.addEventListener('search', () => {
        if (!this.input.value.trim()) {
          clearTimeout(this.searchTimer);
          this.search('');
        }
      }, options);
      this.addEventListener('click', (event) => this.handleClick(event), options);
      document.addEventListener('click', (event) => {
        if (this.activeSlot !== null && !this.panel.contains(event.target) && !this.selector(this.activeSlot)?.contains(event.target)) {
          this.closePicker(false);
        }
      }, options);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.activeSlot !== null) {
          event.preventDefault();
          this.closePicker();
        }
      }, options);
      document.addEventListener('focusin', (event) => {
        if (this.activeSlot !== null && !this.panel.contains(event.target) && !this.selector(this.activeSlot)?.contains(event.target)) {
          this.closePicker(false);
        }
      }, options);
      window.addEventListener('resize', () => this.positionPicker(), options);
      document.addEventListener('scroll', () => this.positionPicker(), { ...options, capture: true, passive: true });
      document.addEventListener('shopify:block:select', (event) => {
        if (event.target && !this.contains(event.target)) return;
        this.jumpToRow(event.detail?.blockId);
      }, options);
      this.renderSelection();
      if (!initial.length && this.dataset.preselect === 'true') {
        const seen = new Set();
        [...this.suggestions.querySelectorAll('[data-compare-add]')].filter((button) => {
          const id = button.dataset.productId;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        }).slice(0, this.slots.length).forEach((button, slot) => this.addProduct(button.dataset, slot));
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      clearTimeout(this.searchTimer);
      this.searchRequest?.abort();
      this.searchSequence += 1;
      this.selected?.forEach((entry) => entry.request.abort());
    }

    selector(slot) {
      return this.headings?.querySelector('[data-compare-open="' + slot + '"]');
    }

    openPicker(slot) {
      if (!Number.isInteger(slot) || slot < 0 || slot >= this.slots.length) return;
      if (this.activeSlot === slot) {
        this.closePicker();
        return;
      }
      this.closePicker(false);
      this.activeSlot = slot;
      this.input.value = '';
      this.search('');
      this.panel.hidden = false;
      this.selector(slot)?.setAttribute('aria-expanded', 'true');
      this.positionPicker();
      this.input.focus({ preventScroll: true });
    }

    closePicker(restoreFocus = true) {
      const slot = this.activeSlot;
      this.activeSlot = null;
      clearTimeout(this.searchTimer);
      this.searchRequest?.abort();
      this.searchSequence += 1;
      this.results?.removeAttribute('aria-busy');
      if (this.panel) this.panel.hidden = true;
      if (slot !== null && slot !== undefined) {
        const button = this.selector(slot);
        button?.setAttribute('aria-expanded', 'false');
        if (restoreFocus) button?.focus({ preventScroll: true });
      }
    }

    positionPicker() {
      if (this.activeSlot === null || !this.panel || this.panel.hidden) return;
      const button = this.selector(this.activeSlot);
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight;
      if (rect.bottom < 0 || rect.top > viewportHeight || rect.right < 0 || rect.left > viewportWidth) {
        this.closePicker(false);
        return;
      }
      const margin = 16;
      const width = Math.min(460, Math.max(400, rect.width), viewportWidth - margin * 2);
      const rtl = getComputedStyle(this).direction === 'rtl';
      const left = Math.max(margin, Math.min(rtl ? rect.right - width : rect.left, viewportWidth - width - margin));
      const below = viewportHeight - rect.bottom - margin - 8;
      const above = rect.top - margin - 8;
      const placeAbove = below < 240 && above > below;
      const height = Math.max(120, Math.min(520, placeAbove ? above : below));
      Object.assign(this.panel.style, {
        left: left + 'px', width: width + 'px', maxHeight: height + 'px',
        top: placeAbove ? 'auto' : Math.max(margin, rect.bottom + 8) + 'px',
        bottom: placeAbove ? Math.max(margin, viewportHeight - rect.top + 8) + 'px' : 'auto'
      });
    }

    handleClick(event) {
      const target = event.target.closest('button, a');
      if (!target || !this.contains(target)) return;
      if (target.matches('[data-compare-open]')) {
        this.openPicker(Number(target.dataset.compareOpen));
      } else if (target.matches('[data-compare-close]')) {
        this.closePicker();
      } else if (target.matches('[data-compare-add]')) {
        const slot = this.activeSlot;
        if (slot === null) return;
        this.addProduct(target.dataset, slot);
        this.closePicker();
      } else if (target.matches('[data-compare-remove]')) {
        const slot = Number(target.dataset.compareRemove);
        this.removeProduct(this.slots[slot]?.id);
        this.selector(slot)?.focus({ preventScroll: true });
      } else if (target.matches('[data-compare-clear]')) {
        this.clearProducts();
        this.selector(0)?.focus({ preventScroll: true });
      } else if (target.matches('[data-compare-add-column]')) {
        if (this.slots.length >= this.limit) return;
        event.stopPropagation();
        this.slots.push(null);
        this.renderSelection();
        const slot = this.slots.length - 1;
        this.selector(slot)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        this.openPicker(slot);
      } else if (target.matches('[data-compare-page]')) {
        event.preventDefault();
        const query = this.displayedQuery || this.input.value.trim();
        this.input.value = query;
        this.search(query, target.getAttribute('href'), true);
      } else if (target.matches('[data-compare-jump]')) {
        event.preventDefault();
        this.jumpToRow(target.dataset.compareJump);
      }
    }

    jumpToRow(key) {
      const row = this.rows.find((item) => item.dataset.compareKey === key);
      if (!row) return;
      const target = row.dataset.compareType === 'info' ? this.headings : row;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      target.scrollIntoView({ block: 'start', behavior });
      this.querySelectorAll('[data-compare-jump]').forEach((link) => {
        if (link.dataset.compareJump === key) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }

    format(value, replacements = {}) {
      return Object.entries(replacements).reduce((text, [key, replacement]) => {
        return text.split('__' + key + '__').join(String(replacement));
      }, value || '');
    }

    announce(message) {
      if (this.status) this.status.textContent = message || '';
      if (this.status?.dataset) {
        this.status.dataset.error = String(message === this.dataset.searchErrorText || message === this.dataset.productErrorText);
      }
    }

    sectionURL(value, kind) {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Invalid comparison URL');
      }
      if (kind === 'product' && !/(?:^|\/)products\/[^/]+\/?$/.test(url.pathname)) {
        throw new Error('Invalid product URL');
      }
      if (kind === 'search') {
        const search = new URL(this.dataset.searchUrl, window.location.origin);
        if (url.pathname !== search.pathname) throw new Error('Invalid search URL');
      }
      url.searchParams.set('section_id', this.dataset.sectionId);
      return url;
    }

    async fetchSection(url, signal) {
      const response = await fetch(url.toString(), { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Comparison request failed');
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const section = [...documentFragment.querySelectorAll('compare-products')]
        .find((element) => element.dataset.sectionId === this.dataset.sectionId);
      if (!section) throw new Error('Comparison section unavailable');
      return section;
    }

    async search(query, pageURL, focusResults = false) {
      this.searchRequest?.abort();
      const sequence = ++this.searchSequence;
      if (!query) {
        this.displayedQuery = '';
        this.results.replaceChildren(...[...this.suggestions.childNodes].map((node) => node.cloneNode(true)));
        this.results.removeAttribute('aria-busy');
        this.updateButtons();
        this.announce(this.results.querySelector('[data-compare-add]')
          ? this.results.querySelector('h2')?.textContent
          : this.results.textContent.trim());
        return;
      }
      const request = new AbortController();
      this.searchRequest = request;
      this.results.setAttribute('aria-busy', 'true');
      this.announce(this.dataset.loadingText);
      try {
        const url = this.sectionURL(pageURL || this.dataset.searchUrl, 'search');
        url.searchParams.set('q', query);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        const section = await this.fetchSection(url, request.signal);
        const results = section.querySelector('[data-compare-results]');
        if (!results) throw new Error('Search results unavailable');
        if (!this.isConnected || request.signal.aborted || sequence !== this.searchSequence) return;
        this.results.replaceChildren(...results.childNodes);
        this.displayedQuery = query;
        this.updateButtons();
        this.announce(this.results.querySelector('[data-compare-add]')
          ? this.results.querySelector('h2')?.textContent
          : this.results.textContent.trim());
        this.positionPicker();
        if (focusResults) {
          const heading = this.results.querySelector('h2');
          heading?.setAttribute('tabindex', '-1');
          heading?.focus();
        }
      } catch (error) {
        if (this.isConnected && !request.signal.aborted && sequence === this.searchSequence && error.name !== 'AbortError') {
          this.announce(this.dataset.searchErrorText);
        }
      } finally {
        if (this.searchRequest === request && sequence === this.searchSequence) this.results.removeAttribute('aria-busy');
      }
    }

    ensureSlots() {
      if (!this.slots) this.slots = Array(Math.min(3, this.limit)).fill(null);
    }

    async addProduct(data, requestedSlot) {
      this.ensureSlots();
      const id = String(data.productId || '');
      if (!id || [...this.selected.values()].some((entry) => entry.id === id || entry.previous?.id === id)) return;
      let slot = requestedSlot;
      if (slot === undefined || slot === null) {
        slot = this.slots.findIndex((entry) => !entry);
        if (slot === -1 && this.slots.length < this.limit) slot = this.slots.push(null) - 1;
      }
      if (!Number.isInteger(slot) || slot < 0 || slot >= this.limit || slot >= this.slots.length) {
        this.announce(this.format(this.dataset.maxText, { LIMIT: this.limit }));
        return;
      }
      const current = this.slots[slot];
      current?.request.abort();
      if (current) this.selected.delete(current.id);
      const entry = {
        id, slot, title: data.productTitle || '', cells: null,
        previous: current?.cells ? current : current?.previous,
        request: new AbortController()
      };
      this.slots[slot] = entry;
      this.selected.set(id, entry);
      this.renderSelection();
      this.announce(this.dataset.loadingText);
      try {
        const url = this.sectionURL(data.productUrl, 'product');
        const section = await this.fetchSection(url, entry.request.signal);
        const template = section.querySelector('template[data-compare-column]');
        if (!template || template.dataset.productId !== id) throw new Error('Product unavailable');
        const cells = new Map([...template.content.querySelectorAll('td[data-compare-key]')]
          .map((cell) => [cell.dataset.compareKey, cell]));
        if (this.rows.some((row) => !cells.has(row.dataset.compareKey))) {
          throw new Error('Product comparison incomplete');
        }
        if (!this.isConnected || entry.request.signal.aborted || this.slots[slot] !== entry) return;
        entry.cells = cells;
        entry.hero = template.content.querySelector?.('[data-compare-product-hero]');
        entry.previous = null;
        this.renderSelection();
        this.announce(this.format(this.dataset.countText, { COUNT: this.selected.size, LIMIT: this.limit }));
      } catch (error) {
        if (!this.isConnected || entry.request.signal.aborted || this.slots[slot] !== entry) return;
        this.selected.delete(id);
        this.slots[slot] = entry.previous || null;
        if (entry.previous) this.selected.set(entry.previous.id, entry.previous);
        this.renderSelection();
        if (error.name !== 'AbortError') this.announce(this.dataset.productErrorText);
      }
    }

    removeProduct(id) {
      const entry = this.selected.get(id);
      if (!entry) return;
      entry.request.abort();
      this.selected.delete(id);
      if (this.slots) this.slots[entry.slot] = null;
      this.renderSelection();
      this.announce(this.format(this.dataset.countText, { COUNT: this.selected.size, LIMIT: this.limit }));
    }

    clearProducts() {
      this.closePicker(false);
      this.selected.forEach((entry) => entry.request.abort());
      this.selected.clear();
      this.slots = Array(Math.min(3, this.limit)).fill(null);
      this.renderSelection();
      this.announce(this.format(this.dataset.countText, { COUNT: 0, LIMIT: this.limit }));
    }

    updateButtons() {
      this.results.querySelectorAll('[data-compare-add]').forEach((button) => {
        const entry = [...this.selected.values()].find((item) => item.id === button.dataset.productId || item.previous?.id === button.dataset.productId);
        button.disabled = Boolean(entry);
        const action = button.querySelector('[data-compare-option-action]');
        if (action) action.textContent = entry
          ? (entry.cells || entry.previous ? this.dataset.selectedText : this.dataset.loadingText)
          : this.dataset.selectText;
      });
    }

    renderSelection() {
      this.ensureSlots();
      const focused = this.headings.contains(document.activeElement) ? document.activeElement : null;
      const focusedSlot = focused?.dataset.compareOpen ?? focused?.dataset.compareRemove;
      this.headings.querySelectorAll('th:not(:first-child)').forEach((cell) => cell.remove());
      this.slots.forEach((entry, slot) => {
        const displayed = entry?.cells ? entry : entry?.previous || entry;
        const heading = document.createElement('th');
        heading.scope = 'col';
        heading.className = 'compare-products__column';
        heading.dataset.slot = String(slot);
        if (entry && !entry.cells) heading.setAttribute('aria-busy', 'true');
        const hero = displayed?.hero?.cloneNode(true);
        const image = hero?.querySelector('.compare-products__product-image');
        const meta = hero?.querySelector('.compare-products__product-meta');
        if (image) heading.append(image);
        else {
          const placeholder = document.createElement('div');
          placeholder.className = 'compare-products__placeholder';
          const icon = document.createElement('span');
          icon.className = 'compare-products__placeholder-icon';
          icon.textContent = '+';
          icon.setAttribute('aria-hidden', 'true');
          const text = document.createElement('span');
          text.className = 'compare-products__placeholder-text';
          text.textContent = entry ? this.dataset.loadingText : this.dataset.emptyColumnText;
          placeholder.append(icon, text);
          heading.append(placeholder);
        }
        const anchor = document.createElement('div');
        anchor.className = 'compare-products__picker-anchor';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'compare-products__selector';
        button.dataset.compareOpen = String(slot);
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-expanded', String(this.activeSlot === slot));
        if (this.panel?.id) button.setAttribute('aria-controls', this.panel.id);
        button.setAttribute('aria-label', displayed
          ? this.format(this.dataset.changeText, { PRODUCT: displayed.title })
          : this.dataset.chooseText + ' ' + (slot + 1));
        const title = document.createElement('span');
        title.className = 'compare-products__selector-title';
        title.textContent = displayed?.title || this.dataset.chooseText;
        const arrow = document.createElement('span');
        arrow.className = 'compare-products__selector-icon';
        const chevron = this.querySelector('template[data-compare-chevron]');
        if (chevron?.content) arrow.append(chevron.content.cloneNode(true));
        arrow.setAttribute('aria-hidden', 'true');
        button.append(title, arrow);
        anchor.append(button);
        heading.append(anchor);
        if (meta) heading.append(meta);
        if (entry) {
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'compare-products__remove';
          remove.dataset.compareRemove = String(slot);
          const removeIcon = this.querySelector('template[data-compare-remove-icon]');
          if (removeIcon?.content) remove.append(removeIcon.content.cloneNode(true));
          remove.setAttribute('aria-label', this.format(this.dataset.removeText, { PRODUCT: displayed.title }));
          heading.append(remove);
        }
        this.headings.append(heading);
      });
      this.rows.forEach((row) => {
        row.querySelectorAll('td').forEach((cell) => cell.remove());
        this.slots.forEach((entry) => {
          const displayed = entry?.cells ? entry : entry?.previous;
          let cell = displayed?.cells?.get(row.dataset.compareKey)?.cloneNode(true);
          if (!cell) {
            cell = document.createElement('td');
            cell.textContent = entry ? this.dataset.loadingText : '—';
          }
          row.append(cell);
        });
      });
      const count = this.selected.size;
      const countElement = this.querySelector('[data-compare-count]');
      if (countElement) countElement.textContent = this.format(this.dataset.countText, { COUNT: count, LIMIT: this.limit });
      const clear = this.querySelector('[data-compare-clear]');
      if (clear) clear.hidden = count === 0;
      const empty = this.querySelector('[data-compare-empty]');
      if (empty) empty.hidden = count > 0;
      const one = this.querySelector('[data-compare-one]');
      if (one) one.hidden = count !== 1;
      const addColumn = this.querySelector('[data-compare-add-column]');
      if (addColumn) addColumn.hidden = this.slots.length >= this.limit;
      const categories = this.querySelector('[data-compare-categories]');
      if (categories) categories.hidden = count === 0 && this.dataset.designMode !== 'true';
      this.tableContainer.hidden = false;
      this.style.setProperty('--compare-product-count', this.slots.length);
      this.updateButtons();
      if (focusedSlot !== undefined) this.selector(Number(focusedSlot))?.focus({ preventScroll: true });
      this.positionPicker();
    }
  }

  customElements.define('compare-products', CompareProducts);
}
